import * as Yup from 'yup';
import { format as formatDate } from 'date-fns';

import { CollapseSection } from '@/components/ats';

import type { DarwinboxFormField } from './api';

const DARWINBOX_DATE_OUTPUT_FORMAT = 'dd/MM/yyyy';

const sectionWrapperProps = {
  title: 'EMPLOYEE DETAILS',
  isHeaderClickable: true,
  defaultIsOpen: true,
  headerConfig: {
    containerStyles: () => ({
      backgroundColor: 'ats-tertiary',
      p: '15px',
    }),
    textStyles: { color: 'ats-primary' },
    iconStyles: { width: '24px', height: '24px' },
  },
  styles: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '6px',
  },
  collapseStyles: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1em',
    backgroundColor: 'white',
  },
};

const buildFieldConfig = (field: DarwinboxFormField) => {
  const base = {
    name: field.id,
    label: field.label,
    isRequired: field.required,
    placeholder: `Enter ${field.label}`,
    optionalValidation: true,
  } as Record<string, any>;

  switch (field.type) {
    case 'string': {
      const validation = field.required
        ? Yup.string().required(`${field.label} is required`)
        : Yup.string();
      return {
        ...base,
        type: 'input',
        validation,
      };
    }
    case 'date': {
      const validation = field.required
        ? Yup.mixed().required(`${field.label} is required`)
        : Yup.mixed();
      return {
        ...base,
        type: 'date',
        // Form state holds a DD/MM/YYYY string after user interaction. Initial
        // prefilled values stay as Date objects — submit-time conversion in
        // submitEmployeeForm normalizes both cases.
        dateFormat: (date: Date) =>
          date ? formatDate(date, DARWINBOX_DATE_OUTPUT_FORMAT) : '',
        validation,
      };
    }
    case 'enum': {
      const options = (field.options || []).map((opt) => ({
        label: opt,
        value: opt,
      }));
      const validation = field.required
        ? Yup.mixed().required(`${field.label} is required`)
        : Yup.mixed();
      return {
        ...base,
        type: 'select',
        options,
        valueKey: 'value',
        labelKey: 'label',
        isClearable: true,
        validation,
      };
    }
    default:
      return null;
  }
};

const parseIsoToDate = (iso: string): Date | null => {
  // BE sends `YYYY-MM-DD`. Build a Date at local midnight to avoid TZ shifts.
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) {
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const [, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d));
};

export interface DarwinboxConvertedSchema {
  formConfig: any[];
  initialValues: Record<string, any>;
}

export const convertDarwinboxSchemaToFormConfig = (
  fields: DarwinboxFormField[] = []
): DarwinboxConvertedSchema => {
  const initialValues: Record<string, any> = {};

  const fieldConfigs = fields
    .map((field) => {
      if (field.value !== null && field.value !== undefined) {
        if (field.type === 'date') {
          const dateObj = parseIsoToDate(String(field.value));
          if (dateObj) initialValues[field.id] = dateObj;
        } else {
          initialValues[field.id] = field.value;
        }
      }
      return buildFieldConfig(field);
    })
    .filter(Boolean);

  const formConfig = [
    {
      id: 'darwinbox-employee',
      type: 'wrapper',
      Wrapper: CollapseSection,
      wrapperProps: sectionWrapperProps,
      fields: fieldConfigs,
    },
  ];

  return { formConfig, initialValues };
};

// Date fields in the form hold either a `DD/MM/YYYY` string (after the user
// picks via the date picker — see `dateFormat` callback) or a `Date` instance
// (when the BE prefilled and the user never opened the picker). Normalize both
// to the wire format Darwinbox expects.
export const normalizeDarwinboxValuesForSubmit = (
  values: Record<string, any>
): Record<string, any> => {
  const result: Record<string, any> = {};
  Object.entries(values).forEach(([key, raw]) => {
    if (raw instanceof Date) {
      result[key] = formatDate(raw, DARWINBOX_DATE_OUTPUT_FORMAT);
      return;
    }
    result[key] = raw;
  });
  return result;
};
